import { Box } from "@chakra-ui/react";
import { ColumnsType, ColumnType } from "antd/lib/table";
import Currency from "components/Currency";
import { Roles, useIdentity } from "context/auth";
import {
    CompletedPaymentType,
    CompletedPaymentTypeNames,
    PayoutRequest,
    PayoutRequestMethod,
    PayoutRequestStatus,
} from "models/payoutRequests";
import { localizedDate, localizedDateAndTime } from "util/helpers";

import PayoutRequestActions from "./PayoutRequestActions";

export const STATUS_LABELS: Record<PayoutRequestStatus, string> = {
    [PayoutRequestStatus.Cancelled]: "Cancelled",
    [PayoutRequestStatus.Pending]: "Pending",
    [PayoutRequestStatus.Complete]: "Completed",
    [PayoutRequestStatus.Unknown]: "Unknown",
    [PayoutRequestStatus.InfoNeeded]: "Info. Needed",
    [PayoutRequestStatus.Submitted]: "Submitted to Bank"
};

export const METHOD_LABELS: Record<PayoutRequestMethod, string> = {
    [PayoutRequestMethod.Paypal]: "Paypal",
    [PayoutRequestMethod.Wire]: "Wire",
    [PayoutRequestMethod.ACH]: "ACH",
    [PayoutRequestMethod.Check]: "Check",
    [PayoutRequestMethod.Other]: "Other",
    [PayoutRequestMethod.Unknown]: "Unknown",
    [PayoutRequestMethod.InternationalWire]: "Intl. Wire"
};

const getColumns = (flat?: boolean): ColumnsType<PayoutRequest> =>
    [
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (status: PayoutRequestStatus) => (
                <Box fontWeight="semibold">{STATUS_LABELS[status]}</Box>
            )
        },
        {
            title: "Progress",
            dataIndex: "progress"
        },
        {
            title: "Request Date",
            dataIndex: "requestDate",
            key: "requestDate",
            sorter: true,
            render: localizedDate,
        },
        {
            title: "Payout Type",
            dataIndex: "method",
            key: "method",
            render: (method: PayoutRequestMethod) => METHOD_LABELS[method],
        },
        {
            title: "Payment Type",
            dataIndex: "paymentType",
            sorter: false,
            render: (pt: CompletedPaymentType) => CompletedPaymentTypeNames[pt]
        },
        {
            title: "Transfered Out",
            dataIndex: "grossAmountInCents",
            key: "grossAmountInCents",
            sorter: true,
            align: "right",
            render: (a, { status }) =>
                status === PayoutRequestStatus.Cancelled ? (
                    <Currency cents={a} textDecor="line-through" opacity={0.5} flat={flat} />
                ) : (
                    <Currency cents={a} flat={flat} />
                ),
        },
        {
            title: "Incurred Fees",
            dataIndex: "feesInCents",
            key: "feesInCents",
            sorter: true,
            align: "right",
            render: (a, { status }) =>
                status === PayoutRequestStatus.Cancelled ? (
                    <Currency cents={-a} textDecor="line-through" opacity={0.5} flat={flat} />
                ) : (
                    <Currency cents={-a} flat={flat} />
                ),
        },
        {
            title: "Payout Amount",
            dataIndex: "netAmountInCents",
            key: "netAmountInCents",
            sorter: true,
            align: "right",
            render: (a, { status }) =>
                status === PayoutRequestStatus.Cancelled ? (
                    <Currency cents={a} textDecor="line-through" opacity={0.5} flat={flat} />
                ) : (
                    <Currency cents={a} flat={flat} />
                ),
        },
        {
            title: "Submission Date",
            dataIndex: "submissionDate",
            sorter: true,
            render: localizedDateAndTime
        },
        {
            key: "actions",
            render: (request: PayoutRequest) =>
                (request.status === PayoutRequestStatus.Pending || request.status === PayoutRequestStatus.InfoNeeded) && (
                    <PayoutRequestActions request={request} />
                ),
        }
    ];

const usePayoutColumns = (exclude: string[] = []): ColumnsType<PayoutRequest> => {
    const { isInRole } = useIdentity();
    const isAdmin = isInRole([Roles.Admin, Roles.SeniorStaff]);

    const columns = getColumns(isAdmin);

    const includedColumns = columns.filter(c => {
        const { dataIndex } = (c as ColumnType<PayoutRequest>);
        if (!dataIndex) {
            return true;
        }

        return !exclude.includes(dataIndex.toString());
    });

    return isAdmin
        ? [
            {
                title: "From",
                dataIndex: "requestingUserFirstName",
                key: "requestingUserFirstName",
                render: (
                    _,
                    { requestingUserFirstName, requestingUserLastName }
                ) => `${requestingUserFirstName} ${requestingUserLastName}`,
            },
            ...includedColumns,
        ]
        : includedColumns;
};

export default usePayoutColumns;
